import { computed, inject } from '@angular/core';

import { updateState, withDevtools, withDevToolsStub, withGlitchTracking } from '@angular-architects/ngrx-toolkit';
import { signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';

import { ConfigService } from '../services/config/config.service';
import { InsuranceClaimService } from '../services/insurance/insurance-claim.service';
import { FarmStore } from './farm.store';
import { NotificationStore } from './notification.store';
import { SuccessNotification } from '../models/notifications/app-notification';
import { InsuranceClaim } from '../entities/insurance/insurance-claim.entities';
import {
    InsuranceClaimQueryParams,
    InsuranceClaimFilters,
    InsuranceClaimCreateRequest,
    InsuranceClaimUpdateRequest,
    InsuranceClaimStatistics
} from '../entities/insurance/insurance-claim-api.types';
import { EntityCreatedResponse } from '../models/api/api-response.model';
import { withCondition } from './utils/with-condition';
import { upsertMany } from '../helpers/data.helpers';

interface InsuranceClaimState {
    claims: InsuranceClaim[];
    selectedClaim: InsuranceClaim | null;
    loading: boolean;
    error: string | null;
    totalClaims: number;
    currentPage: number;
    pageSize: number;
    hasMoreClaims: boolean;
    searchQuery: string;
    filters: InsuranceClaimFilters;
    claimStatistics: InsuranceClaimStatistics | null;
}

const initialState: InsuranceClaimState = {
    claims: [],
    selectedClaim: null,
    loading: false,
    error: null,
    totalClaims: 0,
    currentPage: 0,
    pageSize: 100,
    hasMoreClaims: false,
    searchQuery: '',
    filters: {},
    claimStatistics: null,
};

export const InsuranceClaimStore = signalStore(
    withState(initialState),
    withProps(() => ({
        configService: inject(ConfigService),
        claimService: inject(InsuranceClaimService),
        farmStore: inject(FarmStore),
        notificationStore: inject(NotificationStore),
    })),
    withComputed(({ claims, selectedClaim, loading, error, totalClaims, hasMoreClaims, claimStatistics }) => ({
        hasClaims: computed(() => claims().length > 0),
        claimsCount: computed(() => claims().length),
        totalClaimsCount: computed(() => totalClaims()),
        isLoading: computed(() => loading()),
        hasError: computed(() => error() !== null),
        selectedClaimNumber: computed(() => selectedClaim()?.claim_number || null),
        hasMoreClaimsAvailable: computed(() => hasMoreClaims()),
        hasClaimStatistics: computed(() => claimStatistics() !== null),
    })),
    withCondition(({ configService }) => !configService?.environment?.production,
        withDevtools('insurance-claim', withGlitchTracking()),
        withDevToolsStub('insurance-claim'),
    ),
    withMethods((store) => ({
        loadClaims(params?: InsuranceClaimQueryParams, filters?: InsuranceClaimFilters, append = false): void {
            updateState(store, '[InsuranceClaim] Load Claims Start', { loading: true, error: null });

            // Automatically include current farm ID in filters
            const currentFarmId = store.farmStore.currentFarm()?.id;
            const farmFilters = {
                ...filters,
                ...(currentFarmId && { farm_id: currentFarmId })
            };

            store.claimService.getClaims(params, farmFilters).subscribe({
                next: (response) => {
                    const newClaims = append ? upsertMany(store.claims(), response.data) : response.data;
                    updateState(store, '[InsuranceClaim] Load Claims Success', {
                        claims: newClaims,
                        totalClaims: response.total,
                        currentPage: params?.skip ? Math.floor((params.skip + response.data.length) / (params?.limit || 100)) : 0,
                        pageSize: params?.limit || 100,
                        hasMoreClaims: (params?.skip || 0) + response.data.length < response.total,
                        loading: false,
                        error: null,
                        filters: filters || {},
                        searchQuery: ''
                    });
                },
                error: (error) => {
                    updateState(store, '[InsuranceClaim] Load Claims Error', {
                        loading: false,
                        error: error.message || 'Failed to load claims'
                    });
                }
            });
        },

        searchClaims(query: string, params?: Omit<InsuranceClaimQueryParams, 'q'>): void {
            updateState(store, '[InsuranceClaim] Search Claims Start', { loading: true, error: null, searchQuery: query });

            const currentFarmId = store.farmStore.currentFarm()?.id;
            const searchParams = { ...params, q: query };
            const farmFilters = {
                ...(currentFarmId && { farm_id: currentFarmId })
            };

            store.claimService.getClaims(searchParams, farmFilters).subscribe({
                next: (response) => {
                    const mergedClaims = upsertMany(store.claims(), response.data);
                    updateState(store, '[InsuranceClaim] Search Claims Success', {
                        claims: mergedClaims,
                        loading: false,
                        error: null,
                        searchQuery: query
                    });
                },
                error: (error) => {
                    updateState(store, '[InsuranceClaim] Search Claims Error', {
                        loading: false,
                        error: error.message || 'Failed to search claims'
                    });
                }
            });
        },

        loadClaimById(id: string): void {
            updateState(store, '[InsuranceClaim] Load Claim By ID Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Insurance Claim] Load Claim By ID Error', {
                    loading: false,
                    error: 'Farm ID is required to get claim'
                });
                return;
            }

            store.claimService.getClaimById(id, farmId).subscribe({
                next: (claim) => {
                    const existingIndex = store.claims().findIndex(c => c.id === id);
                    const newClaims = [...store.claims()];
                    if (existingIndex >= 0) {
                        newClaims[existingIndex] = claim;
                    } else {
                        newClaims.push(claim);
                    }

                    updateState(store, '[InsuranceClaim] Load Claim By ID Success', {
                        claims: newClaims,
                        loading: false,
                        error: null
                    });
                },
                error: (error) => {
                    updateState(store, '[InsuranceClaim] Load Claim By ID Error', {
                        loading: false,
                        error: error.message || 'Failed to load claim'
                    });
                }
            });
        },

        createClaim(claimData: InsuranceClaimCreateRequest): void {
            updateState(store, '[InsuranceClaim] Create Claim Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Insurance Claim] Create Claim Error', {
                    loading: false,
                    error: 'Farm ID is required to create claim'
                });
                return;
            }

            store.claimService.createClaim(claimData, farmId).subscribe({
                next: (response: EntityCreatedResponse) => {
                    const newClaim: InsuranceClaim = {
                        ...claimData,
                        id: response.id,
                        farm_id: farmId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };

                    updateState(store, '[InsuranceClaim] Create Claim Success', {
                        claims: [...store.claims(), newClaim],
                        totalClaims: store.totalClaims() + 1,
                        loading: false,
                        error: null
                    });

                    store.notificationStore.addNotification(new SuccessNotification({
                        summary: 'notifications.success',
                        detail: 'insurance.claims.notifications.created'
                    }));
                },
                error: (error) => {
                    updateState(store, '[InsuranceClaim] Create Claim Error', {
                        loading: false,
                        error: error.message || 'Failed to create claim'
                    });
                }
            });
        },

        updateClaim(id: string, updates: InsuranceClaimUpdateRequest): void {
            updateState(store, '[InsuranceClaim] Update Claim Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Insurance Claim] Update Claim Error', {
                    loading: false,
                    error: 'Farm ID is required to update claim'
                });
                return;
            }

            store.claimService.patchClaim(id, updates, farmId).subscribe({
                next: () => {
                    const currentClaim = store.claims().find(c => c.id === id);
                    if (!currentClaim) {
                        updateState(store, '[InsuranceClaim] Update Claim Error', {
                            loading: false,
                            error: 'Claim not found in store after update'
                        });
                        return;
                    }

                    const updatedClaim: InsuranceClaim = {
                        ...currentClaim,
                        ...updates,
                        updated_at: new Date().toISOString()
                    };

                    const newClaims = store.claims().map(claim =>
                        claim.id === id ? updatedClaim : claim
                    );

                    updateState(store, '[InsuranceClaim] Update Claim Success', {
                        claims: newClaims,
                        loading: false,
                        error: null
                    });

                    if (store.selectedClaim()?.id === id) {
                        updateState(store, '[InsuranceClaim] Update Selected Claim', { selectedClaim: updatedClaim });
                    }

                    store.notificationStore.addNotification(new SuccessNotification({
                        summary: 'notifications.success',
                        detail: 'insurance.claims.notifications.updated'
                    }));
                },
                error: (error) => {
                    updateState(store, '[InsuranceClaim] Update Claim Error', {
                        loading: false,
                        error: error.message || 'Failed to update claim'
                    });
                }
            });
        },

        deleteClaim(id: string): void {
            updateState(store, '[InsuranceClaim] Delete Claim Start', { loading: true, error: null });

            const farmId = store.farmStore.currentFarm()?.id;
            if (!farmId) {
                updateState(store, '[Insurance Claim] Delete Claim Error', {
                    loading: false,
                    error: 'Farm ID is required to delete claim'
                });
                return;
            }

            store.claimService.deleteClaim(id, farmId).subscribe({
                next: () => {
                    const newClaims = store.claims().filter(claim => claim.id !== id);
                    updateState(store, '[InsuranceClaim] Delete Claim Success', {
                        claims: newClaims,
                        totalClaims: Math.max(0, store.totalClaims() - 1),
                        loading: false,
                        error: null
                    });

                    if (store.selectedClaim()?.id === id) {
                        updateState(store, '[InsuranceClaim] Clear Selected Claim', { selectedClaim: null });
                    }
                },
                error: (error) => {
                    updateState(store, '[InsuranceClaim] Delete Claim Error', {
                        loading: false,
                        error: error.message || 'Failed to delete claim'
                    });
                }
            });
        },

        setClaimsData(claims: InsuranceClaim[]): void {
            updateState(store, '[InsuranceClaim] Set Claims Data', { claims, loading: false, error: null });
        },

        setLoading(loading: boolean): void {
            updateState(store, '[InsuranceClaim] Set Loading', { loading });
        },

        selectClaim(claim: InsuranceClaim | null): void {
            updateState(store, '[InsuranceClaim] Select Claim', { selectedClaim: claim });
        },

        selectClaimById(claimId: string): void {
            const claim = store.claims().find(c => c.id === claimId) || null;
            this.selectClaim(claim);
        },

        loadNextPage(): void {
            if (!store.hasMoreClaims()) return;

            const skip = store.currentPage() * store.pageSize();
            const params: InsuranceClaimQueryParams = {
                skip,
                limit: store.pageSize()
            };

            this.loadClaims(params, store.filters(), true);
        },

        setFilters(filters: InsuranceClaimFilters): void {
            updateState(store, '[InsuranceClaim] Set Filters', { filters });
            this.loadClaims(undefined, filters);
        },

        clearFilters(): void {
            updateState(store, '[InsuranceClaim] Clear Filters', { filters: {}, searchQuery: '' });
            this.loadClaims();
        },

        refreshClaims(): void {
            this.loadClaims();
        },

        clearError(): void {
            updateState(store, '[InsuranceClaim] Clear Error', { error: null });
        },

        reset(): void {
            updateState(store, '[InsuranceClaim] Reset', initialState);
        },

        initializeClaims(): void {
            // Load claims for the current farm
            this.loadClaims();
        },

        getClaimById(id: string): InsuranceClaim | undefined {
            return store.claims().find(claim => claim.id === id);
        },

        hasAccessToClaim(claimId: string): boolean {
            return store.claims().some(claim => claim.id === claimId);
        }
    }))
);
